// src/components/PaymentSection.js (With Pagination)

import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../App';
import { api, BASE_URL, resolveFileUrl } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';

function PaymentSection() {
    const { user, currentStationId } = useAppContext();
    const [clients, setClients] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [payments, setPayments] = useState([]);
    const [filteredCampaigns, setFilteredCampaigns] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [paymentProofFile, setPaymentProofFile] = useState(null);
    const [existingPaymentProofURL, setExistingPaymentProofURL] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [deletingPayment, setDeletingPayment] = useState(null);
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [isLoading, setIsLoading] = useState(true);

    // --- NEW STATE FOR PAGINATION ---
    const [pageSize] = useState(15);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPayments, setTotalPayments] = useState(0);
    
    // Fetch clients and all campaigns for the form dropdowns
    useEffect(()=>{
        if(currentStationId === 'all') {
            setClients([]); setCampaigns([]); return;
        };

        const fetchData = async () => {
            try {
                const [clientsData, campaignsData] = await Promise.all([
                    api.get('clients', { where: { stationId: currentStationId } }),
                    api.get('ad_campaigns', { where: { stationId: currentStationId } })
                ]);
                setClients(clientsData);
                setCampaigns(campaignsData);
            } catch (err) {
                console.error(err);
            }
        };
        fetchData();
    }, [currentStationId]);
    
    const fetchPayments = useCallback(async () => {
        if (currentStationId === 'all') {
             setPayments([]); setTotalPayments(0); setIsLoading(false); return;
        }
        setIsLoading(true);

        const startOfMonth = new Date(filterYear, filterMonth - 1, 1).toISOString().split('T')[0];
        const endOfMonth = new Date(filterYear, filterMonth, 0).toISOString().split('T')[0];
        
        try {
            const results = await api.get('payments', { 
                where: { stationId: currentStationId }
            });

            const filtered = results.filter(p => {
                const d = new Date(p.date).toISOString().split('T')[0];
                return d >= startOfMonth && d <= endOfMonth;
            });

            setTotalPayments(filtered.length);
            
            // Manual pagination
            const start = (currentPage - 1) * pageSize;
            const end = start + pageSize;
            setPayments(filtered.slice(start, end));

        } catch (err) {
            toast.error("Error fetching payments.");
        } finally {
            setIsLoading(false);
        }

    }, [currentStationId, filterYear, filterMonth, pageSize, currentPage]);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    useEffect(()=>{
        if(selectedClientId){
            setFilteredCampaigns(campaigns.filter(c => c.clientId === selectedClientId));
        } else {
            setFilteredCampaigns([]);
        }
        setSelectedCampaignId('');
    }, [selectedClientId, campaigns]);

    const handleEditClick = (payment) => {
        setEditingPayment(payment);
        setSearchTerm(payment.clientName);
        setSelectedClientId(payment.clientId);
        setTimeout(() => setSelectedCampaignId(payment.campaignId), 0);
        setAmount(payment.amount);
        setPaymentMethod(payment.paymentMethod);
        setExistingPaymentProofURL(payment.proofURL || payment.paymentProofURL || '');
        setPaymentProofFile(null);
        window.scrollTo(0, 0);
    };

    const resetForm = () => {
        setEditingPayment(null); setAmount(''); setSelectedCampaignId('');
        setSelectedClientId(''); setSearchTerm(''); setPaymentMethod('Cash');
        setPaymentProofFile(null);
        setExistingPaymentProofURL('');
    };

    const updateInvoiceTotal = async (campaign) => {
        if (!campaign) return;
        try {
            const campaignPayments = await api.get('payments', { where: { campaignId: campaign.id } });
            const totalPaidForCampaign = campaignPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
            const pendingAmountForCampaign = (parseFloat(campaign.totalCampaignAmount) || 0) - totalPaidForCampaign;

            let newStatus = 'Unpaid';
            if (pendingAmountForCampaign <= 0) newStatus = 'Paid';
            else if (totalPaidForCampaign > 0) newStatus = 'Partially Paid';
            
            const existingInvoices = await api.get('invoices', { where: { campaignId: campaign.id }, limit: 1 });

            if (existingInvoices.length > 0) {
                const invoiceId = existingInvoices[0].id;
                const updateData = { 
                    status: newStatus,
                    totalPaid: totalPaidForCampaign,
                    pendingAmount: pendingAmountForCampaign
                };
                await api.put('invoices', invoiceId, updateData);
            }
        } catch (err) {
            console.error("Error updating invoice total:", err);
        }
    };
    
    const handleDeletePayment = async () => {
        if (!deletingPayment) return;
        try {
            await api.delete('payments', deletingPayment.id);
            toast.success("Payment deleted successfully!");
            const campaign = campaigns.find(c => c.id === deletingPayment.campaignId);
            if (campaign) {
                await updateInvoiceTotal(campaign);
            }
            fetchPayments();
        } catch (err) {
            toast.error("Failed to delete payment.");
        } finally {
            setDeletingPayment(null);
        }
    };

    const handleAddOrUpdatePayment = async(e) => {
        e.preventDefault();
        if(!selectedCampaignId || !amount) {
            toast.error("Please select client, campaign and enter an amount.");
            return;
        }
        const proofRequired = paymentMethod === 'Cheque' || paymentMethod === 'Account Transfer';
        if (proofRequired && !paymentProofFile && !existingPaymentProofURL) {
            toast.error("Please upload payment proof (screenshot) for Cheque / Account Transfer.");
            return;
        }
        setIsSubmitting(true);

        const campaign = campaigns.find(c => c.id === selectedCampaignId);
        const client = clients.find(c => c.id === selectedClientId);

        if(!campaign || !client) {
            toast.error("Could not find client or campaign details.");
            setIsSubmitting(false);
            return;
        }

        let proofURL = existingPaymentProofURL || '';
        const uploadProof = async (file) => {
            const fd = new FormData();
            fd.append('photo', file);
            const response = await fetch(`${BASE_URL}/api/upload`, { method: 'POST', body: fd });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Payment proof upload failed');
            }
            const result = await response.json();
            return result.filePath;
        };

        const paymentData = {
            clientId: selectedClientId, clientName: client.name, campaignId: selectedCampaignId,
            campaignName: campaign.mainCampaignName || campaign.campaignName, stationId: currentStationId,
            amount: parseFloat(amount), paymentMethod: paymentMethod, updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
        };

        try {
            if (paymentProofFile) {
                proofURL = await uploadProof(paymentProofFile);
            }
            if (proofURL) {
                paymentData.proofURL = proofURL;
            }
            if (editingPayment) {
                await api.put('payments', editingPayment.id, paymentData);
                toast.success("Payment updated successfully!");
            } else {
                paymentData.date = new Date().toISOString().slice(0, 19).replace('T', ' ');
                await api.post('payments', paymentData);
                toast.success('Payment added successfully!');
            }
            await updateInvoiceTotal(campaign);
            resetForm();
            fetchPayments();
        } catch (err) {
            toast.error(`Failed to process payment: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const filteredClients = clients.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const pageStart = totalPayments > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const pageEnd = Math.min(currentPage * pageSize, totalPayments);

    return(
         <div className="container mx-auto">
             <ConfirmationModal isOpen={!!deletingPayment} onClose={() => setDeletingPayment(null)} onConfirm={handleDeletePayment} title="Delete Payment">Are you sure you want to delete this payment record? This action cannot be undone and may affect invoice totals.</ConfirmationModal>
            <div className="mb-8"><h1 className="text-4xl font-bold text-gray-800">Record a Payment</h1><p className="text-gray-500 mt-1">Add or edit payments and view the transaction history.</p></div>
            {currentStationId === 'all' ? (<div className="text-center p-10 bg-white rounded-2xl shadow-lg"><h3 className="text-xl font-bold text-gray-700">Please select a station</h3><p className="text-gray-500 mt-2">Select a station to manage payments.</p></div>) 
            : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-lg p-8 sticky top-8">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">{editingPayment ? 'Edit Payment' : 'New Payment Entry'}</h2>
                            <form onSubmit={handleAddOrUpdatePayment} className="space-y-6">
                                <div><label className="block text-sm font-medium text-gray-700">1. Search & Select Client</label><input type="text" placeholder="Start typing client name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" /><select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)} className="mt-2 w-full p-3 bg-gray-50 rounded-md" required><option value="">Select a Client</option>{filteredClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                                {selectedClientId && (<div><label className="block text-sm font-medium text-gray-700">2. Select Campaign</label><select value={selectedCampaignId} onChange={e => setSelectedCampaignId(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" required><option value="">Select a Campaign</option>{filteredCampaigns.map(c => (<option key={c.id} value={c.id}>{c.mainCampaignName || c.campaignName} ({new Date(c.startDate).toLocaleString('default', { month: 'long' })})</option>))}</select></div>)}
                                <div><label className="block text-sm font-medium text-gray-700">3. Enter Amount</label><input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="PKR" className="mt-1 w-full p-3 bg-gray-50 rounded-md" required /></div>
                                <div><label className="block text-sm font-medium text-gray-700">4. Payment Method</label><select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md"><option>Cash</option><option>Cheque</option><option>Account Transfer</option></select></div>
                                {(paymentMethod === 'Cheque' || paymentMethod === 'Account Transfer') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">5. Payment Proof (Screenshot)</label>
                                        <input type="file" accept="image/*,application/pdf" onChange={(e) => setPaymentProofFile(e.target.files?.[0] || null)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" required={!existingPaymentProofURL} />
                                        {existingPaymentProofURL && (
                                            <a href={resolveFileUrl(existingPaymentProofURL)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline font-semibold">
                                                View Current Proof
                                            </a>
                                        )}
                                    </div>
                                )}
                                <div className="flex gap-2"><button type="submit" disabled={isSubmitting} className="w-full bg-rose-600 text-white font-bold py-4 rounded-lg shadow-lg hover:bg-rose-700 disabled:bg-gray-400">{isSubmitting ? 'Saving...' : (editingPayment ? 'Update Payment' : 'Record Payment')}</button>{editingPayment && (<button type="button" onClick={resetForm} className="w-1/3 bg-gray-300 text-gray-800 font-bold py-4 rounded-lg">Cancel</button>)}</div>
                            </form>
                        </div>
                    </div>
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="p-6 border-b flex flex-wrap justify-between items-center gap-4">
                            <h2 className="text-2xl font-bold text-gray-800">Payment History</h2>
                            <div className="flex items-center gap-2">
                                <select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="p-2 border rounded-md bg-gray-50">{Array.from({ length: 12 }, (_, i) => (<option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>))}</select>
                                <select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="p-2 border rounded-md bg-gray-50">{Array.from({ length: 5 }, (_, i) => (<option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>))}</select>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                    <tr><th className="px-6 py-3">Date</th><th className="px-6 py-3">Client</th><th className="px-6 py-3">Campaign</th><th className="px-6 py-3">Method</th><th className="px-6 py-3">Proof</th><th className="px-6 py-3 text-right">Amount</th><th className="px-6 py-3 text-center">Actions</th></tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {isLoading ? (<tr><td colSpan="7" className="px-6 py-10 text-center">Loading payments...</td></tr>) : payments.length > 0 ? payments.map(p => (
                                        <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">{new Date(p.date).toLocaleDateString()}</td>
                                            <td className="px-6 py-4 font-semibold text-gray-800">{p.clientName}</td>
                                            <td className="px-6 py-4 text-xs">{p.campaignName}</td>
                                            <td className="px-6 py-4"><span className="px-2 py-1 bg-gray-100 rounded text-xs">{p.paymentMethod}</span></td>
                                            <td className="px-6 py-4 text-xs">
                                                {(p.proofURL || p.paymentProofURL) ? (
                                                    <a href={resolveFileUrl(p.proofURL || p.paymentProofURL)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">
                                                        View
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">--</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right font-bold text-gray-900">PKR {p.amount.toLocaleString()}</td>
                                            <td className="px-6 py-4 text-center whitespace-nowrap">
                                                <button onClick={() => handleEditClick(p)} className="text-rose-600 hover:text-rose-800 font-semibold mr-3">Edit</button>
                                                {user?.role === 'admin' && (<button onClick={() => setDeletingPayment(p)} className="text-gray-400 hover:text-red-600">Delete</button>)}
                                            </td>
                                        </tr>
                                    )) : (<tr><td colSpan="7" className="px-6 py-10 text-center text-gray-400 italic">No payments found for this period.</td></tr>)}
                                </tbody>
                            </table>
                        </div>
                        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-100">
                            <p className="text-xs text-gray-500">Showing <strong>{pageStart}-{pageEnd}</strong> of <strong>{totalPayments}</strong> payments</p>
                            <div className="flex space-x-2">
                                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage * pageSize >= totalPayments} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">Next</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PaymentSection;
